import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Wallet, TrendingUp, PieChart, Lock, User, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const AuthPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  // Login form state
  const [loginForm, setLoginForm] = useState({
    nomeUsuario: '',
    senha: '',
  });
  
  // Register form state
  const [registerForm, setRegisterForm] = useState({
    nome: '',
    nomeUsuario: '',
    senha: '',
    confirmarSenha: '',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.nomeUsuario || !loginForm.senha) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    setIsLoading(true);
    const result = await login(loginForm.nomeUsuario, loginForm.senha);
    setIsLoading(false);
    
    if (result.success) {
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } else {
      toast.error(typeof result.error === 'string' ? result.error : 'Usuário ou senha inválidos');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerForm.nome || !registerForm.nomeUsuario || !registerForm.senha) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    if (registerForm.senha !== registerForm.confirmarSenha) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (registerForm.senha.length < 4) {
      toast.error('A senha deve ter pelo menos 4 caracteres');
      return;
    }
    
    setIsLoading(true);
    const result = await register(registerForm.nome, registerForm.nomeUsuario, registerForm.senha);
    setIsLoading(false);
    
    if (result.success) {
      toast.success('Conta criada com sucesso! Faça login para continuar.');
      setRegisterForm({ nome: '', nomeUsuario: '', senha: '', confirmarSenha: '' });
    } else {
      toast.error(typeof result.error === 'string' ? result.error : 'Erro ao criar conta');
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2" data-testid="auth-page">
      {/* Left Panel - Decorative */}
      <div className="hidden lg:flex flex-col justify-center items-center bg-zinc-900 relative overflow-hidden p-12">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-green-900/40 via-zinc-900 to-black" />
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(34, 197, 94, 0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(34, 197, 94, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>
        
        {/* Content */}
        <div className="relative z-10 text-center max-w-md">
          <div className="flex justify-center mb-8">
            <div className="p-4 rounded-2xl bg-green-500/10 border border-green-500/30 shadow-[0_0_30px_rgba(34,197,94,0.3)]">
              <Wallet className="w-12 h-12 text-green-400" />
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4 font-['Manrope']">
            Controle Financeiro
          </h1>
          <p className="text-zinc-400 text-lg mb-12">
            Gerencie suas finanças com inteligência e simplicidade
          </p>
          
          {/* Feature Cards */}
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-black/40 backdrop-blur-md border border-white/10">
              <div className="p-2 rounded-lg bg-green-500/20">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-left">
                <h3 className="text-white font-medium">Acompanhe seus gastos</h3>
                <p className="text-zinc-500 text-sm">Visualize suas receitas e despesas</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 rounded-xl bg-black/40 backdrop-blur-md border border-white/10">
              <div className="p-2 rounded-lg bg-green-500/20">
                <PieChart className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-left">
                <h3 className="text-white font-medium">Categorize tudo</h3>
                <p className="text-zinc-500 text-sm">Organize por categorias personalizadas</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex flex-col justify-center items-center p-6 md:p-12 bg-black">
        {/* Mobile Logo */}
        <div className="lg:hidden flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30">
            <Wallet className="w-8 h-8 text-green-400" />
          </div>
          <span className="text-2xl font-bold text-white font-['Manrope']">Finanças</span>
        </div>

        <Card className="w-full max-w-md bg-zinc-900/50 border border-white/10 backdrop-blur-md">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold text-white font-['Manrope']">
              Bem-vindo
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Entre ou crie uma conta para continuar
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-zinc-800/50 mb-6">
                <TabsTrigger 
                  value="login" 
                  className="data-[state=active]:bg-green-600 data-[state=active]:text-white"
                  data-testid="login-tab"
                >
                  Entrar
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  className="data-[state=active]:bg-green-600 data-[state=active]:text-white"
                  data-testid="register-tab"
                >
                  Criar Conta
                </TabsTrigger>
              </TabsList>
              
              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-username" className="text-zinc-300">
                      Nome de usuário
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        id="login-username"
                        data-testid="login-username-input"
                        type="text"
                        placeholder="seu_usuario"
                        value={loginForm.nomeUsuario}
                        onChange={(e) => setLoginForm({ ...loginForm, nomeUsuario: e.target.value })}
                        className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white placeholder:text-zinc-600"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-zinc-300">
                      Senha
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={loginForm.senha}
                        onChange={(e) => setLoginForm({ ...loginForm, senha: e.target.value })}
                        className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white placeholder:text-zinc-600"
                      />
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="login-submit-btn"
                    disabled={isLoading}
                    className="w-full bg-green-600 hover:bg-green-500 text-white font-medium shadow-[0_0_15px_rgba(34,197,94,0.4)] transition-all mt-6"
                  >
                    {isLoading ? 'Entrando...' : 'Entrar'}
                  </Button>
                </form>
              </TabsContent>
              
              {/* Register Tab */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-zinc-300">
                      Nome completo
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        id="register-name"
                        data-testid="register-name-input"
                        type="text"
                        placeholder="João Silva"
                        value={registerForm.nome}
                        onChange={(e) => setRegisterForm({ ...registerForm, nome: e.target.value })}
                        className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white placeholder:text-zinc-600"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-username" className="text-zinc-300">
                      Nome de usuário
                    </Label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        id="register-username"
                        data-testid="register-username-input"
                        type="text"
                        placeholder="joao_silva"
                        value={registerForm.nomeUsuario}
                        onChange={(e) => setRegisterForm({ ...registerForm, nomeUsuario: e.target.value })}
                        className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white placeholder:text-zinc-600"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-zinc-300">
                      Senha
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        id="register-password"
                        data-testid="register-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={registerForm.senha}
                        onChange={(e) => setRegisterForm({ ...registerForm, senha: e.target.value })}
                        className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white placeholder:text-zinc-600"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm-password" className="text-zinc-300">
                      Confirmar senha
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        id="register-confirm-password"
                        data-testid="register-confirm-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={registerForm.confirmarSenha}
                        onChange={(e) => setRegisterForm({ ...registerForm, confirmarSenha: e.target.value })}
                        className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white placeholder:text-zinc-600"
                      />
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="register-submit-btn"
                    disabled={isLoading}
                    className="w-full bg-green-600 hover:bg-green-500 text-white font-medium shadow-[0_0_15px_rgba(34,197,94,0.4)] transition-all mt-6"
                  >
                    {isLoading ? 'Criando conta...' : 'Criar Conta'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-zinc-600 text-sm mt-8">
          Projeto de estudos em API REST
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
